import { clearGuestIntakeToken, getGuestIntakeToken } from "@/services/authStorage";
import { claimGuestIntake } from "@/services/quoteIntakeService";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AuthModal } from "@/components/auth/AuthModal";
import { IncomeCertificateWizard } from "@/components/features/notarial-requests/income-certificate-wizard";
import { IncomeCertificateRequestSummary } from "@/components/features/notarial-requests/income-certificate-request-summary";
import { PaymentMethodSelector } from "@/components/features/notarial-requests/payment-method-selector";
import { LegalEntityRequestFields } from "@/components/features/notarial-requests/legal-entity-request-fields";
import { LegalEntityRequestSummary } from "@/components/features/notarial-requests/legal-entity-request-summary";
import { NewRequestIntentSelection } from "@/components/features/notarial-requests/new-request-intent-selection";
import { NewRequestTypeSelection } from "@/components/features/notarial-requests/new-notarial-request-selection";
import { QuoteRequestFields } from "@/components/features/notarial-requests/quote-request-fields";
import { QuoteRequestReview } from "@/components/features/notarial-requests/quote-request-review";
import { QuoteIntakeChat } from "@/components/features/notarial-requests/quote-intake-chat";
import { QuoteIntakeOutOfScope } from "@/components/features/notarial-requests/quote-intake-out-of-scope";
import { QuoteRequestSummaryStep } from "@/components/features/notarial-requests/quote-request-summary";
import { QuoteDeliveryAddressStep } from "@/components/features/notarial-requests/quote-delivery-address-step";
import { isAddressSelectionReady } from "@/components/shared/AddressPicker";
import { createUserAddress } from "@/services/userAddressesService";
import { serverMessage } from "@/lib/apiError";
import { ContactBlockedNotice } from "@/components/features/notarial-requests/contact-blocked-notice";
import { EnterpriseProductCard } from "@/components/catalog/EnterpriseProductCard";
import { GraphWorkflowRunner } from "@/components/features/enterprise/GraphWorkflowRunner";
import type { GraphState, ProductGraph } from "@/services/platformEnterprisesService";
import { getStartNode, getOutgoingEdges, getNodeById, type BranchNodeData } from "@/lib/productGraph";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { WizardAnimatedPanel } from "@/components/shared/WizardAnimatedPanel";
import { WizardStepHeading } from "@/components/shared/WizardStepHeading";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useNewCertificateRequestFlow } from "@/hooks/useNewCertificateRequestFlow";
import { cn } from "@/lib/utils";
import { DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED } from "@/constants/certificateFulfillmentModes";
import { catalogItemForType } from "@/services/certificatePricingCatalogService";
import { linkChildToEnterpriseRequest } from "@/services/enterpriseCatalogService";
import { flowKindForProductType } from "@/utils/certificateRequestFlow";
import { fireRegistrationConversion } from "@/lib/google-ads";
import {
  trackEvent,
  trackRfqAuthGateCompleted,
  trackRfqAuthGateDismissed,
  trackRfqAuthGateShown,
} from "@/lib/mag-analytics";
import type { ConsumerCertificateRequestSource } from "@/services/userAccountantRequestsService";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export function UserNewRequestWizardPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSidebarOpen, hasSidebar } = useSidebar();
  const { user, isLoading: isAuthLoading } = useAuth();
  const isAnonymous = user == null;
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  /**
   * When true, the AuthModal stays open in a "processing" state while the post-auth
   * submit (create checkout → create request → dLocal redirect) is in flight. Without
   * this we'd close the modal immediately after register/signIn, exposing a brief flash
   * of the wizard (or a layout-switch transient) before the dLocal navigation kicks in.
   * Reset when the submit completes without redirecting (failure case handled by toast).
   */
  const [isPostAuthSubmitting, setIsPostAuthSubmitting] = useState(false);
  /** Action to re-fire after the user authenticates via the AuthModal. */
  const pendingPostAuthAction = useRef<(() => void | Promise<void>) | null>(null);
  /*
    Whether the wall the visitor is currently looking at was raised from the
    intake chat. A REF AND NOT STATE: it is read by the modal's close and
    success handlers to label an analytics event, never rendered, so holding it
    in state would re-render the wizard for nothing. Captured when the gate
    opens so the three events agree even if the step moves underneath them.
  */
  const authGateFromIntake = useRef(false);

  const certificatePaymentState = searchParams.get("certificate_payment");
  const paymentResumeCheckoutIdRaw =
    certificatePaymentState === "done" ? searchParams.get("checkout_id") : null;

  const returnEnterpriseRequestId = searchParams.get("returnEnterpriseRequest");
  const returnStepKey = searchParams.get("returnStep");

  /**
   * The catalog product an enterprise workflow node wants a child request for.
   *
   * Gated on `returnEnterpriseRequest` deliberately. Spec
   * `retire-fixed-certificates-for-rfq` retires the fixed-price flow as a
   * consumer entry point; honouring a bare `?requestType=` would leave it one
   * URL away and make the retirement cosmetic. A graph node always sends both.
   *
   * `requestType` rather than `type` because that is the param the two call
   * sites have always sent — `InternalRequestNodeView` and
   * `GraphWorkflowRunner` — and which nothing in the portal has ever read, so
   * those nodes have been landing on the intent picker and relying on the user
   * to hand-pick their way to the product. Hiding the certificates card is
   * what turns that from degraded into impossible.
   */
  const enterpriseProductType = returnEnterpriseRequestId
    ? searchParams.get("requestType")
    : null;

  /**
   * Set by AuthModal when redirecting through Google SSO from the wizard. After SSO
   * returns to /requests/new?postSsoResume=1 the IndexedDB hydrate restores the
   * summary state — we then auto-fire the original submit handler so the visitor
   * doesn't have to manually press "Pay" again.
   */
  const isPostSsoResume = searchParams.get("postSsoResume") === "1";

  useEffect(() => {
    if (certificatePaymentState === "failed") {
      toast.error(t("requests.user.paymentFailed"));
      navigate("/requests/new", { replace: true });
      return;
    }
    if (certificatePaymentState === "timeout") {
      toast.error(t("requests.user.paymentTimeout"));
      navigate("/requests/new", { replace: true });
    }
  }, [certificatePaymentState, navigate, t]);

  const [isLinkingChild, setIsLinkingChild] = useState(false);

  const onCertificateRequestCreated = useCallback(
    (requestId: string, source: ConsumerCertificateRequestSource) => {
      // Guest-checkout: wipe the IndexedDB snapshot now that the request is committed,
      // so a future anonymous visit on the same device doesn't hydrate stale form data.
      clearPersistedWizardSnapshotRef.current?.();
      if (returnEnterpriseRequestId && returnStepKey) {
        setIsLinkingChild(true);
        linkChildToEnterpriseRequest(returnEnterpriseRequestId, returnStepKey, requestId)
          .then(() => {
            toast.success(t("requests.user.wizard.workflowChildLinkedSuccess"));
            navigate(`/requests?tab=enterprise`, { replace: true });
          })
          .catch(() => {
            toast.error(t("requests.user.wizard.workflowChildLinkError"));
            navigate(`/requests/${requestId}?source=${source}`);
          })
          .finally(() => setIsLinkingChild(false));
        return;
      }
      // Profile gate now runs UPSTREAM of the resume runner (see the
      // `shouldGateProfileBeforeResume` effect below), so by the time we
      // get here the visitor has either completed their profile or
      // explicitly skipped — either way they go straight to the detail.
      // Skip-from-gate parks the request in `pending_profile_completion`,
      // and the detail page renders the "complete profile" banner.
      navigate(`/requests/${requestId}?source=${source}`);
    },
    [navigate, returnEnterpriseRequestId, returnStepKey, t],
  );

  /**
   * Stable handle to the hook's `clearPersistedWizardSnapshot`. We need it inside
   * `onCertificateRequestCreated`, which is created BEFORE the hook is invoked. The ref
   * is set right after the hook returns so the callback can access the latest version
   * without re-creating the request-created handler (which would re-mount the hook).
   */
  const clearPersistedWizardSnapshotRef = useRef<(() => void) | null>(null);

  // Session lost across the full-page dLocal round-trip: auth has finished
  // hydrating (`!isAuthLoading`) yet there is still no user, while we hold a
  // paid checkout to resume. Instead of stranding a paying user, prompt a
  // re-auth: the payment is confirmed and the resume blob is still in this
  // device's IndexedDB, so once they sign back in the resume runner takes
  // over automatically from the same URL — no re-upload, no re-charge.
  const isPaymentResumeSessionLost =
    paymentResumeCheckoutIdRaw != null &&
    !returnEnterpriseRequestId &&
    !isAuthLoading &&
    user == null;
  useEffect(() => {
    if (isPaymentResumeSessionLost) setAuthModalOpen(true);
  }, [isPaymentResumeSessionLost]);

  // Run the resume runner as soon as auth has hydrated a user — do NOT gate on
  // profile completeness first. The runner creates the request (the backend
  // lands it in `pending_profile_completion` when the profile is incomplete —
  // see accountant/notarial create()) and uploads the wizard documents from
  // IndexedDB, then routes to the request detail, whose
  // `pending_profile_completion` banner prompts profile completion. Verifying
  // the phone there auto-promotes the request server-side
  // (auth.controller → promoteHeldConsumerRequests).
  //
  // Creating the request BEFORE the profile step is deliberate: a captured
  // payment must ALWAYS materialize a request. Previously the flow redirected
  // to /complete-profile first and deferred the runner, so a user who paid and
  // then abandoned the profile screen left money captured with no request row
  // (incident 2026-07-30). The perceived-order nicety was not worth that hole.
  //
  // While auth is still loading (`user == null`) we defer; if it finished with
  // no user, the session-lost branch above prompts re-auth instead.
  const paymentResumeCheckoutId =
    user != null ? paymentResumeCheckoutIdRaw : null;

  const flow = useNewCertificateRequestFlow({
    onRequestCreated: onCertificateRequestCreated,
    paymentResumeCheckoutId,
    returnEnterpriseRequestId,
    returnStepKey,
    initialWizardType: enterpriseProductType ?? "generic",
    enterpriseCatalogEntry: returnEnterpriseRequestId != null && enterpriseProductType == null,
  });

  const {
    requestType,
    allowedTypes,
    rut,
    validatedCompany,
    legalEntityEffectiveCompany,
    legalEntityManualMode,
    legalEntityManualFields,
    updateLegalEntityManualField,
    isValidatingRut,
    estatutoFile,
    setEstatutoFile,
    comunicacionBcuFile,
    setComunicacionBcuFile,
    legalDocumentType,
    setLegalDocumentType,
    legalDocumentFrontFile,
    setLegalDocumentFrontFile,
    legalDocumentBackFile,
    setLegalDocumentBackFile,
    passportDocumentFile,
    setPassportDocumentFile,
    incomeInnerStep,
    setIncomeInnerStep,
    incomeForm,
    incomePrimaryEnabled,
    setIncomePrimaryEnabled,
    incomeWizardRef,
    incomeFlowPhase,
    catalogQuery,
    certificateRequestIntentsQuery,
    selectedIntentSlug,
    handleIntentChange,
    handleCertificateTypeChange,
    handleRutInputChange,
    handleValidateRut,
    handleContinueFromIntent,
    handleContinueFromType,
    handleContinueLegalEntityToSummary,
    handleLegalEntityFooterPrimary,
    legalEntityPhase,
    submitIncomeCertificate,
    paymentMethod,
    setPaymentMethod,
    incomeReviewPhase,
    incomeReviewPayload,
    incomeChargeTotalLabel,
    handleIncomeStep3ContinueToReview,
    completeIncomeCertificateFromSummary,
    runIncomePrimaryAction,
    handleHeaderBack,
    step,
    isIncomeCertificateFlow,
    isLegalEntityFlow,
    isQuoteRequest,
    isChooseIntent,
    isChooseType,
    isChooseEnterpriseProduct,
    isPreconditionCheck,
    isFillEnterpriseProduct,
    isWorkflowSteps,
    enterpriseCatalogQuery,
    selectedEnterpriseProduct,
    handleSelectEnterpriseProduct,
    handleCompleteWorkflowStep,
    activeEnterpriseRequest,
    workflowViewNodeId,
    pendingStepData,
    enterpriseFormSubmitting,
    isWizardSubmitting,
    isCertificatePaymentBusy,
    isPaymentResumeRunning,
    paymentResumeProgress,
    legalEntityChargeTotalLabel,
    canContinueIntent,
    canContinueType,
    canSubmitLegalEntity,
    legalEntityFulfillmentMode,
    setLegalEntityFulfillmentMode,
    setLegalEntityInvoiceIncludeRut,
    setLegalEntityInvoiceRutDigits,
    setIncomeInvoiceIncludeRut,
    setIncomeInvoiceRutDigits,
    legalEntityInvoiceValid,
    incomeInvoiceValid,
    invoiceRutLockedToRegistration,
    effectiveLegalEntityInvoiceIncludeRut,
    effectiveLegalEntityInvoiceRutDigits,
    effectiveIncomeInvoiceIncludeRut,
    effectiveIncomeInvoiceRutDigits,
    legalEntityCompanyRutMode,
    registeredCompanySnapshot,
    selectLegalEntitySessionCompany,
    selectLegalEntityOtherCompany,
    returnToLegalEntityCompanyChoice,
    snapshotWizardForSsoRedirect,
    clearPersistedWizardSnapshot,
    quoteRequestTitle,
    quoteRequestDescription,
    quoteRequestAttachments,
    setQuoteRequestTitle,
    setQuoteRequestAttachments,
    setQuoteRequestDescription,
    handleQuoteRequestSubmit,
    quoteRequestBlockedKinds,
    handleContinueQuoteRequestToSummary,
    quoteRequestPhase,
    quoteIntakeMessages,
    quoteIntakeThinking,
    quoteIntakeTurnsRemaining,
    quoteIntakeOutOfScopeMessage,
    quoteIntakeConversationId,
    sendOutOfScopeAnyway,
    quoteAnticipatedDocuments,
    quoteDeliveryAddressSelection,
    quoteDeliveryOptedOut,
    setQuoteDeliveryOptedOut,
    setQuoteDeliveryAddressSelection,
    setQuoteDeliveryAddressId,
    handleContinueQuoteRequestToSummaryFromDelivery,
    quoteIntakeAnswers,
    setQuoteIntakeAnswers,
    quoteIntakeDetails,
    quoteDraftDisciplines,
    isQuoteDraftReview,
    handleQuoteIntakeSend,
    fallbackToQuoteRequestForm,
  } = flow;

  // Keep the ref to clearPersistedWizardSnapshot in sync so onCertificateRequestCreated
  // (created before `flow` exists) can call the latest version after a successful
  // request commit. See ref declaration above for the why.
  clearPersistedWizardSnapshotRef.current = clearPersistedWizardSnapshot;

  /**
   * Fire wizard_started exactly once per fresh entry. Skip post-payment and post-SSO
   * re-mounts — the wizard already started in a previous mount, this is the resume.
   */
  const wizardStartedRef = useRef(false);
  useEffect(() => {
    if (wizardStartedRef.current) return;
    if (certificatePaymentState === "done") return;
    if (isPostSsoResume) return;
    wizardStartedRef.current = true;
    trackEvent("wizard_started", {
      wizard_type: searchParams.get("type") ?? "generic",
      // Canonical product key, consistent across the funnel (payment_initiated,
      // request_paid). The real targetType or null on a cold entry — never the
      // "generic" sentinel, so service_type always means "which product".
      service_type: searchParams.get("type"),
    });
  }, [certificatePaymentState, isPostSsoResume, searchParams]);

  /**
   * Mirror the auth gate modal to `?auth=open` so the URL reflects that the
   * visitor is in the signin/signup prompt fired from the summary submit. Lets
   * analytics + support see the gate state at a glance and rounds out the
   * wizard URL mirrors. `replace` so opening/closing the modal doesn't bloat
   * history. All other params (step, type, phase, resume, utm_*) preserved.
   */
  useEffect(() => {
    setSearchParams(
      (current) => {
        const has = current.has("auth");
        if (authModalOpen) {
          if (current.get("auth") === "open") return current;
          const next = new URLSearchParams(current);
          next.set("auth", "open");
          return next;
        }
        if (!has) return current;
        const next = new URLSearchParams(current);
        next.delete("auth");
        return next;
      },
      { replace: true },
    );
  }, [authModalOpen, setSearchParams]);

  /**
   * Enterprise products are out of scope for guest checkout — their workflows (multi-
   * actor invites, staff approvals, internal request branching) require server-side
   * state that anonymous visitors can't hold. Strip them from the intent list when
   * the visitor isn't authenticated so they can't enter a flow they can't finish.
   */
  const visibleIntents = useMemo(() => {
    const all = certificateRequestIntentsQuery.data ?? [];
    if (!isAnonymous) return all;
    return all.filter((i) => i.flowKey !== "enterprise_catalog");
  }, [certificateRequestIntentsQuery.data, isAnonymous]);

  const incomeCertificateCatalogItem = catalogItemForType(
    catalogQuery.data ?? [],
    "income_certificate",
  );
  const incomeFulfillmentModes =
    incomeCertificateCatalogItem?.fulfillmentModesAllowed != null &&
    incomeCertificateCatalogItem.fulfillmentModesAllowed.length > 0
      ? incomeCertificateCatalogItem.fulfillmentModesAllowed
      : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];

  const legalEntityCatalogItem = catalogItemForType(
    catalogQuery.data ?? [],
    "notarial_legal_entity",
  );
  const legalEntityFulfillmentOptions =
    legalEntityCatalogItem?.fulfillmentModesAllowed != null &&
    legalEntityCatalogItem.fulfillmentModesAllowed.length > 0
      ? legalEntityCatalogItem.fulfillmentModesAllowed
      : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];

  // Compute an initial GraphState from the product graph for rendering the first
  // interactive node before a server-side request exists.
  const initialGraphState = useMemo((): GraphState => {
    const empty: GraphState = { currentNodeId: null, completedPath: [], completedNodes: {}, childRequestIds: {} };
    const graph = selectedEnterpriseProduct?.graph as ProductGraph | null;
    if (!graph) return empty;
    const start = getStartNode(graph);
    if (!start) return empty;
    const firstEdge = getOutgoingEdges(graph, start.id)[0];
    if (!firstEdge) return empty;

    let nodeId = firstEdge.target;
    const completedPath = [start.id];
    const AUTO_TYPES = new Set(["branch", "pricing"]);

    for (let i = 0; i < graph.nodes.length; i++) {
      const node = getNodeById(graph, nodeId);
      if (!node) break;

      // Auto-advance past automatic nodes
      if (AUTO_TYPES.has(node.type)) {
        completedPath.push(nodeId);
        const edges = getOutgoingEdges(graph, nodeId);

        let nextEdge = edges[0];
        if (node.type === 'branch' && edges.length > 1) {
          // Evaluate branch condition against accumulated pending values
          const allFlat: Record<string, unknown> = {};
          for (const stepValues of Object.values(pendingStepData)) {
            Object.assign(allFlat, stepValues);
          }
          const branchData = node.data as unknown as BranchNodeData;
          const fieldValue = allFlat[branchData.field];
          const matchedCase = (branchData.cases ?? []).find((c) => {
            const fv = Number(fieldValue);
            const cv = Number(c.value);
            switch (c.op) {
              case 'equals': return String(fieldValue) === String(c.value);
              case 'gt': return fv > cv;
              case 'gte': return fv >= cv;
              case 'lt': return fv < cv;
              case 'lte': return fv <= cv;
              default: return false;
            }
          });
          if (matchedCase) {
            const matched = edges.find((e) => e.sourceHandle === matchedCase.id || e.data?.caseId === matchedCase.id);
            if (matched) nextEdge = matched;
          } else {
            const defaultEdge = edges.find((e) => e.sourceHandle === 'default' || e.data?.isDefault);
            if (defaultEdge) nextEdge = defaultEdge;
          }
        }

        if (!nextEdge) break;
        nodeId = nextEdge.target;
        continue;
      }

      // Advance past interactive nodes that have pending data (collected client-side)
      if (pendingStepData[nodeId]) {
        completedPath.push(nodeId);
        const edges = getOutgoingEdges(graph, nodeId);
        let nextEdge;
        if (node.type === 'precondition') {
          nextEdge = edges.find((e) => e.sourceHandle === 'pass') ?? edges[0];
        } else if (node.type === 'decision') {
          // Follow the edge matching the chosen option value
          const stepData = pendingStepData[nodeId];
          const outputKey = (node.data as { outputKey?: string }).outputKey;
          const chosenValue = outputKey ? String(stepData[outputKey] ?? '') : '';
          nextEdge = edges.find((e) => e.sourceHandle === chosenValue)
            ?? edges.find((e) => e.sourceHandle === 'default' || e.data?.isDefault)
            ?? edges[0];
        } else {
          nextEdge = edges[0];
        }
        if (!nextEdge) break;
        nodeId = nextEdge.target;
        continue;
      }

      break; // Stop at the first interactive node without data
    }
    return { currentNodeId: nodeId, completedPath, completedNodes: {}, childRequestIds: {} };
  }, [selectedEnterpriseProduct?.graph, pendingStepData]);

  const workflowStepHeading = useMemo((): { lead: string; rest: string } | null => {
    const graph = selectedEnterpriseProduct?.graph as ProductGraph | null;
    if (!graph) return null;
    const currentState = activeEnterpriseRequest?.workflowState
      ? (activeEnterpriseRequest.workflowState as GraphState)
      : initialGraphState;
    const nodeId = workflowViewNodeId ?? currentState.currentNodeId;
    if (!nodeId) return null;
    const node = getNodeById(graph, nodeId);
    if (!node) return null;
    const label = (node.data as { label?: string }).label;
    const desc = (node.data as { description?: string }).description;
    return label ? { lead: label, rest: desc ?? "" } : null;
  }, [selectedEnterpriseProduct?.graph, activeEnterpriseRequest?.workflowState, initialGraphState, workflowViewNodeId]);

  const workflowRunnerState = useMemo((): GraphState => {
    const base = activeEnterpriseRequest?.workflowState
      ? (activeEnterpriseRequest.workflowState as GraphState)
      : initialGraphState;
    if (workflowViewNodeId) {
      return { ...base, currentNodeId: workflowViewNodeId };
    }
    return base;
  }, [activeEnterpriseRequest?.workflowState, initialGraphState, workflowViewNodeId]);

  // Auto-trigger createRequest node: when the computed current node is createRequest,
  // fire handleCompleteWorkflowStep to create the request and redirect.
  const createRequestTriggered = useRef(false);
  useEffect(() => {
    if (!isWorkflowSteps || activeEnterpriseRequest) return;
    const graph = selectedEnterpriseProduct?.graph as ProductGraph | null;
    if (!graph) return;
    const curNodeId = initialGraphState.currentNodeId;
    if (!curNodeId) return;
    const curNode = getNodeById(graph, curNodeId);
    if (!curNode || curNode.type !== 'createRequest') return;
    if (createRequestTriggered.current) return;
    createRequestTriggered.current = true;
    void handleCompleteWorkflowStep(curNodeId, {});
  }, [isWorkflowSteps, activeEnterpriseRequest, selectedEnterpriseProduct?.graph, initialGraphState.currentNodeId, handleCompleteWorkflowStep]);

  /**
   * Guest-checkout helper: anonymous visitors hitting a payment-bound action open the
   * AuthModal instead of firing the action. After successful auth (modal `onSuccess`),
   * we re-fire the captured action so the visitor never has to re-click. Returning users
   * already authenticated take the original code path with no detour.
   */
  /**
   * Hand the guest conversation to the account the visitor just authenticated
   * into, and forget the guest credential either way.
   *
   * The token is cleared in BOTH branches and in the failure path. Leaving it
   * behind would mean a later anonymous visit on this browser — or this same
   * visitor after logging out — picking up a credential for someone else's
   * conversation, which is worse than losing the link.
   */
  const settleGuestIntake = useCallback(
    async (authPath: "register" | "signIn" | "google"): Promise<void> => {
      const guestToken = getGuestIntakeToken();
      if (!guestToken) return;
      try {
        /*
          ONLY EMAIL REGISTER PROMOTES THE ROW IN PLACE — it keeps the guest's
          user id, so the conversation is already theirs and there is nothing
          to move. Sign-in and Google SSO both land on an account that exists
          SEPARATELY from the guest row, so the work has to be claimed onto it.

          Google was missing from this list and that was the bug: the SSO
          handler mints a fresh user from the Google profile and knows nothing
          about the guest token, so neither half of the settlement ran.
        */
        if (authPath !== "register") {
          await claimGuestIntake(guestToken);
        }
      } catch {
        // Deliberately silent — see the call site.
      } finally {
        clearGuestIntakeToken();
      }
    },
    [],
  );

  /*
    THE OTHER HALF OF THE GATE, and for a while it did not exist.

    `onSuccess` on the AuthModal is where a visitor's guest conversation gets
    settled and where the gate's `completed` event fires — and AuthModal.tsx
    documents that onSuccess is "NOT called for the Google SSO path — that's a
    full-page redirect". So a visitor who chatted as a guest and then signed up
    with Google had their conversation silently orphaned: the guest row kept
    `status='guest'`, the new account had nothing, and the draft they had just
    spent ten messages building was gone. Confirmed in production on
    2026-10-19 with a real consumer who did exactly that.

    SEPARATE FROM THE RESUME EFFECT BELOW, deliberately. That one re-fires the
    pending submit and gates on a step being ready — `incomeCertificate`,
    `legalEntityDetails` or `chooseType`. The intake chat is none of those, so
    the step the guest funnel actually uses would have skipped this entirely.
    Settlement must not depend on which wizard step the visitor is on.

    `fromIntake` IS "a guest token was present", not the live step: the page
    reloaded through Google, so the ref that held it is gone. A guest token
    only ever exists because the intake chat minted one, which makes its
    presence the honest answer to the question the event asks.
  */
  const postSsoSettleFiredRef = useRef(false);
  useEffect(() => {
    if (postSsoSettleFiredRef.current) return;
    if (!isPostSsoResume || isAnonymous) return;
    postSsoSettleFiredRef.current = true;
    trackRfqAuthGateCompleted({
      fromIntake: getGuestIntakeToken() != null,
      authPath: "google",
    });
    void settleGuestIntake("google");
  }, [isPostSsoResume, isAnonymous, settleGuestIntake]);

  /*
    THE ONE SITE THAT MEANS "WALL" (spec rfq-auth-gate-instrumentation).

    `setAuthModalOpen(true)` is called from four places in this file and only
    this one is a gate. The post-SSO resume below opens the SAME modal as a
    progress spinner for someone who has already authenticated, and the
    payment-resume screen opens it for a logged-out returning payer. So the
    emit lives inside `gateOnAuth` rather than in a `useEffect` keyed on
    `authModalOpen`, which would read all four as walls and inflate the
    denominator by every SSO completion — the exact number this measures.

    `isQuoteRequest` / `quoteRequestPhase` and NOT `isQuoteIntakeChat`: that
    const is declared ~400 lines below this callback, so naming it here would
    read it before initialisation.
  */
  const gateOnAuth = useCallback(
    (action: () => void | Promise<void>): void => {
      if (isAnonymous) {
        pendingPostAuthAction.current = action;
        authGateFromIntake.current = isQuoteRequest && quoteRequestPhase === "chat";
        trackRfqAuthGateShown({ fromIntake: authGateFromIntake.current });
        setAuthModalOpen(true);
        return;
      }
      void action();
    },
    [isAnonymous, isQuoteRequest, quoteRequestPhase],
  );

  // Auto-advance: when user picks a certificate type, skip the "Siguiente" button.
  // For simple notary/accountant types this fires the submit immediately, so anonymous
  // visitors land at the AuthModal here; income / legal_entity types just advance the
  // step (their submit happens later at the summary, where we gate again).
  useEffect(() => {
    if (isChooseType && requestType != null) {
      const kind = flowKindForProductType(requestType, catalogQuery.data);
      const isSimpleSubmit = kind === "simple_notary" || kind === "simple_accountant";
      if (isSimpleSubmit) {
        gateOnAuth(() => handleContinueFromType());
        return;
      }
      handleContinueFromType();
    }
  }, [isChooseType, requestType, handleContinueFromType, catalogQuery.data, gateOnAuth]);

  /**
   * Legacy consumer deep links: `?type=<product>` and the `?phase=` the wizard
   * used to write beside it.
   *
   * Both named the fixed-price certificate flow, which spec
   * `retire-fixed-certificates-for-rfq` retires as a consumer entry point.
   * They are STRIPPED rather than redirected: no app URL appears in any
   * sitemap, so there is no ranking to preserve — these are bookmarks and
   * shared links the wizard put in people's address bars itself.
   *
   * Stripped once, behind a ref, and only when the visitor did NOT arrive from
   * an enterprise workflow node. The wizard writes `?type=` and `?phase=`
   * itself as it walks its steps, so a standing strip would fight its own URL
   * writing and a refresh would lose the step — which is why this runs exactly
   * once, before any of that can happen.
   */
  const legacyParamsStrippedRef = useRef(false);
  useEffect(() => {
    if (legacyParamsStrippedRef.current) return;
    legacyParamsStrippedRef.current = true;
    if (returnEnterpriseRequestId) return;
    if (!searchParams.has("type") && !searchParams.has("phase")) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("type");
        next.delete("phase");
        return next;
      },
      { replace: true },
    );
  }, [returnEnterpriseRequestId, searchParams, setSearchParams]);

  /**
   * Deep-link support for a FLOW rather than a product: `?intent=quote_request`
   * drops the visitor straight into the bespoke-request intake.
   *
   * `?type=` cannot express this. It looks the value up in the certificate
   * catalog and then selects the `certificate_catalog` intent, which is right
   * for a fixed-price product and wrong for an RFQ — whose whole premise is
   * that the trámite is NOT in that catalog. The landing page's "your trámite
   * isn't listed" entry point needs this one.
   *
   * Validated against the intents the visitor can actually use (`visibleIntents`),
   * so an anonymous link into a flow that requires an account selects nothing
   * instead of stranding them in it. Consumed once via a ref so backing up
   * does not re-select.
   *
   * Largely redundant since the sole remaining intent auto-selects below, but
   * kept: the landing site links with it, and a link that still resolves is
   * cheaper than coordinating two deploys.
   */
  const deepLinkIntent = searchParams.get("intent");

  /**
   * What the visitor typed in the landing hero's ask box, carried over as
   * `?q=`.
   *
   * SEEDS the composer; it is never auto-sent. `onSend` below is wrapped in
   * `gateOnAuth`, so an anonymous arrival from the landing — which is most of
   * them — would hit the auth modal on a turn they never pressed send on. They
   * land on their own sentence instead, already typed.
   */
  const heroAskDraft = searchParams.get("q");
  const deepLinkConsumedRef = useRef(false);

  useEffect(() => {
    if (!deepLinkIntent || deepLinkConsumedRef.current) return;
    if (isPostSsoResume) {
      deepLinkConsumedRef.current = true;
      return;
    }
    if (!isChooseIntent) return;
    if (visibleIntents.length === 0) return;
    const match = visibleIntents.find(
      (i) => i.slug === deepLinkIntent && i.status === "active",
    );
    deepLinkConsumedRef.current = true;
    // Unknown, inactive, or not available to this visitor: fall through to the
    // ordinary picker rather than getting stuck on a flow that cannot start.
    if (!match) return;
    handleIntentChange(match.slug);
  }, [deepLinkIntent, isPostSsoResume, isChooseIntent, visibleIntents, handleIntentChange]);

  /**
   * With the certificates card retired there is one thing to choose, and a
   * one-card picker is a click that asks nothing. Auto-select it so
   * `/requests/new` lands straight on the intake chat (spec
   * `retire-fixed-certificates-for-rfq`, control C-3).
   *
   * Conditioned on exactly one ACTIVE intent and nothing in `coming_soon`:
   * a coming-soon card is worth showing even beside a single live option,
   * because it says what is on the way. Re-enable a second intent in the
   * admin and the picker comes back on its own, with no code change.
   *
   * Skipped during a post-SSO resume for the same reason the deep links are:
   * the IndexedDB hydrate is restoring a later step, and selecting an intent
   * underneath it would fight that.
   */
  const soleSelectableIntent = useMemo(() => {
    const active = visibleIntents.filter((i) => i.status === "active");
    const comingSoon = visibleIntents.filter((i) => i.status === "coming_soon");
    return active.length === 1 && comingSoon.length === 0 ? active[0] : null;
  }, [visibleIntents]);

  const soleIntentAppliedRef = useRef(false);
  useEffect(() => {
    if (soleIntentAppliedRef.current) return;
    if (isPostSsoResume || deepLinkIntent) return;
    // An enterprise arrival routes itself, in the hook, once the catalog
    // query lands. This effect only needs the intents query, so without this
    // guard whichever query resolved first would win the race and a workflow
    // node could end up in the RFQ chat.
    if (returnEnterpriseRequestId) return;
    if (!isChooseIntent || !soleSelectableIntent) return;
    soleIntentAppliedRef.current = true;
    handleIntentChange(soleSelectableIntent.slug);
  }, [
    isPostSsoResume,
    deepLinkIntent,
    returnEnterpriseRequestId,
    isChooseIntent,
    soleSelectableIntent,
    handleIntentChange,
  ]);

  /**
   * Post-SSO resume: when the URL carries `?postSsoResume=1` AND the visitor is now
   * authenticated AND the wizard has hydrated to a payment-bound state, auto-fire the
   * original submit handler. Without this the visitor lands on the summary screen and
   * has to manually click "Pay" again — the SSO redirect was effectively the click,
   * so re-clicking is friction we can eliminate.
   *
   * Gated on:
   *   - `isPostSsoResume` (set by AuthModal's Google handler)
   *   - `!isAnonymous` (auth state actually flipped)
   *   - hydrate has landed us in a summary state with the required payload
   *
   * Fires exactly once via a ref guard; clears the URL param via replace navigation
   * so refreshing the page doesn't re-trigger.
   */
  const postSsoResumeFiredRef = useRef(false);
  useEffect(() => {
    if (postSsoResumeFiredRef.current) return;
    if (!isPostSsoResume) return;
    if (isAnonymous) return;
    const incomeReady =
      step === "incomeCertificate" &&
      incomeReviewPhase === "summary" &&
      incomeReviewPayload != null;
    const legalReady =
      step === "legalEntityDetails" &&
      legalEntityPhase === "summary" &&
      canSubmitLegalEntity;
    const simpleReady = step === "chooseType" && requestType != null;
    if (!incomeReady && !legalReady && !simpleReady) return;

    postSsoResumeFiredRef.current = true;
    setIsPostAuthSubmitting(true);
    setAuthModalOpen(true);
    // Clear the URL param immediately so a refresh mid-flight doesn't re-trigger.
    navigate("/requests/new", { replace: true });

    const fire = async (): Promise<void> => {
      try {
        if (incomeReady) {
          await completeIncomeCertificateFromSummary();
        } else if (legalReady) {
          await handleLegalEntityFooterPrimary();
        } else if (simpleReady) {
          await handleContinueFromType();
        }
      } finally {
        setIsPostAuthSubmitting(false);
        setAuthModalOpen(false);
      }
    };
    void fire();
  }, [
    isPostSsoResume,
    isAnonymous,
    step,
    incomeReviewPhase,
    incomeReviewPayload,
    legalEntityPhase,
    canSubmitLegalEntity,
    requestType,
    completeIncomeCertificateFromSummary,
    handleLegalEntityFooterPrimary,
    handleContinueFromType,
    navigate,
  ]);

  const showHeaderBack = isIncomeCertificateFlow || isLegalEntityFlow || isQuoteRequest || isChooseType || isChooseEnterpriseProduct || isPreconditionCheck || isFillEnterpriseProduct || isWorkflowSteps;

  /**
   * Back out of the wizard entirely when there was nothing to choose.
   *
   * `handleHeaderBack` returns to the intent picker, which is right while
   * there are intents to pick between. With the certificates card retired
   * there is one, auto-selected above — so "back" would land the visitor on a
   * single-card screen whose only move is forward again, and the auto-select
   * will not re-fire (it is consumed once, or it would loop). Leaving to
   * /requests is the honest destination.
   *
   * Only from the intake chat's first phase: deeper steps have their own
   * meaningful back targets inside the flow, which this must not swallow.
   */
  const handleBack = useCallback((): void => {
    if (soleSelectableIntent && isQuoteRequest && quoteRequestPhase === "chat") {
      navigate("/requests");
      return;
    }
    handleHeaderBack();
  }, [soleSelectableIntent, isQuoteRequest, quoteRequestPhase, navigate, handleHeaderBack]);

  /** Drives enter animation when the visible wizard panel changes. */
  const wizardPanelTransitionKey = [
    step,
    isLegalEntityFlow ? legalEntityPhase : "-",
    isIncomeCertificateFlow ? `${incomeReviewPhase}-${incomeInnerStep}` : "-",
    isQuoteRequest ? quoteRequestPhase : "-",
  ].join("|");

  const wizardScrollAnchorRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    wizardScrollAnchorRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
  }, [wizardPanelTransitionKey]);

  const handleFooterPrimary = (): void => {
    if (isChooseIntent) {
      handleContinueFromIntent();
      return;
    }
    if (isChooseType) {
      const kind = flowKindForProductType(requestType ?? "", catalogQuery.data);
      const isSimpleSubmit = kind === "simple_notary" || kind === "simple_accountant";
      if (isSimpleSubmit) {
        gateOnAuth(() => handleContinueFromType());
        return;
      }
      handleContinueFromType();
      return;
    }
    if (isLegalEntityFlow) {
      if (legalEntityPhase === "form") {
        handleContinueLegalEntityToSummary();
        return;
      }
      // legalEntityPhase === "summary" is the payment-bound submit.
      gateOnAuth(() => handleLegalEntityFooterPrimary());
      return;
    }
    if (isQuoteRequest) {
      if (quoteRequestPhase === "chat") {
        // The conversation drives itself; the footer button is the opt-out
        // into the static form rather than a "next".
        fallbackToQuoteRequestForm();
        return;
      }
      if (quoteRequestPhase === "form") {
        /*
          THE GATE (spec b5b59843). "The agent has figured out what you need,
          here is the button" — this is that button, and the account is asked
          for before it does anything.

          It is also the LAST place it can be asked: the next step creates a
          delivery address through POST /me/addresses, which an intake-scoped
          guest cannot call. Moving the gate any later would mean building
          anonymous address writes too.

          gateOnAuth re-fires this same action after the modal succeeds, so a
          visitor who signs up here continues into the delivery step rather
          than being returned to a screen they already finished.
        */
        gateOnAuth(() => handleContinueQuoteRequestToSummary());
        return;
      }
      if (quoteRequestPhase === "delivery") {
        void handleQuoteDeliveryContinue();
        return;
      }
      // quoteRequestPhase === "summary" — no payment involved, just the real submit.
      gateOnAuth(() => handleQuoteRequestSubmit());
      return;
    }
  };

  const [isSavingDeliveryAddress, setIsSavingDeliveryAddress] = useState(false);
  // Raised by the delivery step when the chosen address has no priceable zone
  // (control C-3). Blocking here rather than letting them continue is the
  // point: the next screen states a total, and we would have no shipping
  // figure to put in it.
  const [isDeliveryUnpriceable, setIsDeliveryUnpriceable] = useState(false);

  /**
   * Turn the picked address into an id the request can reference.
   *
   * A NEW address is created through the ordinary /me/addresses endpoint
   * first, rather than sent inline with the request. That keeps one path that
   * writes an address — validated, owned, Place-derived — instead of a second
   * one here whose rules would drift from it, and it means the consumer's new
   * address lands in their book where they can reuse and edit it.
   *
   * A failure keeps the consumer ON this step. Advancing to a summary that
   * states a shipping total we could not record would show them a price for a
   * destination the request does not have.
   */
  const handleQuoteDeliveryContinue = async (): Promise<void> => {
    /*
      The escape short-circuits everything below it: there is no address to
      save, no id to carry, and nothing to price. The flag travels on the
      create payload instead.
    */
    if (quoteDeliveryOptedOut) {
      setQuoteDeliveryAddressId(null);
      handleContinueQuoteRequestToSummaryFromDelivery();
      return;
    }
    const selection = quoteDeliveryAddressSelection;
    if (!selection) return;
    if (selection.kind === "saved") {
      setQuoteDeliveryAddressId(selection.addressId);
      handleContinueQuoteRequestToSummaryFromDelivery();
      return;
    }
    setIsSavingDeliveryAddress(true);
    try {
      const created = await createUserAddress(selection.payload);
      setQuoteDeliveryAddressId(created.id);
      // Re-point the selection at the saved row so going back and forward
      // does not create a duplicate address on every pass.
      setQuoteDeliveryAddressSelection({
        kind: "saved",
        addressId: created.id,
        contactPhone: created.contactPhone ?? null,
        contactPersonName: created.contactPersonName ?? null,
        courierNotes: created.courierNotes ?? null,
      });
      handleContinueQuoteRequestToSummaryFromDelivery();
    } catch (err) {
      toast.error(serverMessage(err) ?? t("requests.user.quoteDeliverySaveError"));
    } finally {
      setIsSavingDeliveryAddress(false);
    }
  };

  const footerPrimaryDisabled = isIncomeCertificateFlow
    ? incomeReviewPhase === "summary"
      ? isWizardSubmitting || incomeReviewPayload == null || !incomeInvoiceValid
      : isWizardSubmitting || !incomePrimaryEnabled
    : isChooseIntent
      ? isWizardSubmitting ||
        !canContinueIntent ||
        certificateRequestIntentsQuery.isPending
      : isChooseEnterpriseProduct || isPreconditionCheck || isFillEnterpriseProduct || isWorkflowSteps
        ? true
        : isChooseType
          ? isWizardSubmitting || !canContinueType
          : isLegalEntityFlow
            ? legalEntityPhase === "form"
              ? isWizardSubmitting || !canSubmitLegalEntity
              : isWizardSubmitting || !legalEntityInvoiceValid
            : isQuoteRequest
              ? quoteIntakeOutOfScopeMessage != null
                ? true
                : quoteRequestPhase === "chat"
                ? false
                : quoteRequestPhase === "form"
                ? isWizardSubmitting ||
                  quoteRequestTitle.trim().length === 0 ||
                  quoteRequestDescription.trim().length === 0
                : quoteRequestPhase === "delivery"
                ? isWizardSubmitting ||
                  isSavingDeliveryAddress ||
                  isDeliveryUnpriceable ||
                  !isAddressSelectionReady(quoteDeliveryAddressSelection)
                : isWizardSubmitting
              : true;

  const nonIncomeSubmittingSpinner =
    isWizardSubmitting &&
    (isLegalEntityFlow ||
      (isQuoteRequest && quoteRequestPhase === "summary") ||
      (isChooseType &&
        requestType != null &&
        requestType !== "notarial_legal_entity" &&
        requestType !== "income_certificate"));

  /**
   * The conversation owns its own controls — the send button is the primary
   * action and the opt-out sits under the composer — so the wizard footer
   * would only add a second, louder button doing something else.
   */
  const isQuoteIntakeChat =
    isQuoteRequest && quoteRequestPhase === "chat" && quoteIntakeOutOfScopeMessage == null;

  /** Reviewing the conversation's draft, as opposed to filling the fallback form. */
  const isQuoteDraftReviewStep =
    isQuoteRequest && quoteRequestPhase === "form" && isQuoteDraftReview;

  let quoteRequestHeading: { lead: string; rest: string };
  if (isQuoteIntakeChat) {
    quoteRequestHeading = {
      lead: "requests.user.wizard.quoteRequestChatLead",
      rest: "requests.user.wizard.quoteRequestChatRest",
    };
  } else if (isQuoteRequest && quoteRequestPhase === "summary") {
    quoteRequestHeading = {
      lead: "requests.user.wizard.quoteRequestSummaryLead",
      rest: "requests.user.wizard.quoteRequestSummaryRest",
    };
  } else if (isQuoteDraftReviewStep) {
    // The conversation already wrote this. Asking them to "describí tu
    // solicitud con el mayor detalle posible" over a filled-in form is asking
    // for work that was just done for them.
    quoteRequestHeading = {
      lead: "requests.user.wizard.quoteRequestReviewLead",
      rest: "requests.user.wizard.quoteRequestReviewRest",
    };
  } else {
    quoteRequestHeading = {
      lead: "requests.user.wizard.quoteRequestLead",
      rest: "requests.user.wizard.quoteRequestRest",
    };
  }

  let footerPrimaryLabel: string;
  if (isIncomeCertificateFlow) {
    if (isWizardSubmitting) {
      footerPrimaryLabel =
        incomeFlowPhase === "payment"
          ? t("requests.user.paymentOpeningCheckout")
          : t("requests.user.creatingRequest");
    } else if (incomeReviewPhase === "summary") {
      footerPrimaryLabel = t("requests.user.proceedWithPayment");
    } else {
      footerPrimaryLabel = t("pages.common.continue");
    }
  } else if (nonIncomeSubmittingSpinner) {
    footerPrimaryLabel = isCertificatePaymentBusy
      ? t("requests.user.paymentOpeningCheckout")
      : t("requests.user.creatingRequest");
  } else if (isChooseIntent) {
    footerPrimaryLabel = t("pages.common.continue");
  } else if (isChooseType) {
    if (requestType === "notarial_legal_entity") {
      footerPrimaryLabel = t("requests.user.nextStep");
    } else if (requestType === "income_certificate") {
      footerPrimaryLabel = t("requests.user.incomeWizard.continueToDetails");
    } else if (requestType != null) {
      footerPrimaryLabel = t("requests.user.createRequest");
    } else {
      footerPrimaryLabel = t("pages.common.continue");
    }
  } else if (isLegalEntityFlow) {
    footerPrimaryLabel =
      legalEntityPhase === "summary"
        ? t("requests.user.proceedWithPayment")
        : t("pages.common.continue");
  } else if (isQuoteRequest) {
    footerPrimaryLabel =
      quoteRequestPhase === "summary"
        ? t("requests.user.createRequest")
        : t("pages.common.continue");
  } else {
    footerPrimaryLabel = t("pages.common.continue");
  }

  /** Wide content area; income flow needs room for modality row + forms. */
  const maxW = "w-full max-w-5xl 2xl:max-w-6xl";

  // Session was lost across the dLocal round-trip — prompt re-auth instead of
  // flashing the empty wizard.
  if (isPaymentResumeSessionLost) {
    const resumeBackUrl = `/requests/new?certificate_payment=done&checkout_id=${encodeURIComponent(
      paymentResumeCheckoutIdRaw!,
    )}`;
    return (
      <>
        <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-6 px-6 text-center">
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {t("requests.user.paymentResume.sessionLostTitle", "Tu pago está confirmado")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t(
                "requests.user.paymentResume.sessionLostDescription",
                "Tu sesión se cerró al volver del pago. Iniciá sesión de nuevo para completar y enviar tu solicitud — no vas a tener que volver a cargar tus documentos ni pagar otra vez.",
              )}
            </p>
          </div>
          <Button
            size="lg"
            className="w-full"
            onClick={() => setAuthModalOpen(true)}
          >
            {t("requests.user.paymentResume.sessionLostCta", "Iniciar sesión para continuar")}
          </Button>
        </div>
        <AuthModal
          open={authModalOpen}
          surface="standalone_login"
          onOpenChange={setAuthModalOpen}
          onSuccess={(authPath) => {
            // This modal offers register alongside sign-in, so an account can be
            // created here and this mount owes the same signup conversion as the
            // wizard-submit mount below. The helper is sessionStorage-de-duped,
            // so a visitor who already registered before paying is not counted
            // twice when they come back through this screen.
            if (authPath === "register") {
              fireRegistrationConversion();
            }
            setAuthModalOpen(false);
          }}
          // No wizard state to preserve here — SSO returns straight to the
          // resume URL, which re-runs the gate + resume runner from IndexedDB.
          onBeforeSsoRedirect={() => undefined}
          ssoReturnTo={resumeBackUrl}
        />
      </>
    );
  }

  if (isPaymentResumeRunning) {
    // When we already know the profile is incomplete, the resume flow ends
    // on /complete-profile — not on the request detail. Using "Creando tu
    // solicitud / vinculándola al pago" sets the wrong expectation in that
    // case (user thinks it's about to be sent to a professional). Use
    // softer copy that primes the upcoming profile step instead.
    const profileIncomplete =
      user != null &&
      (!user.profileImage ||
        user.profileImage.length === 0 ||
        user.phoneVerifiedAt == null);
    const resumeTitle =
      paymentResumeProgress?.phase === "creating_request"
        ? profileIncomplete
          ? t("requests.user.paymentResume.savingTitle", "Guardando tu solicitud")
          : t("requests.user.paymentResume.creatingTitle", "Creando tu solicitud")
        : paymentResumeProgress?.phase === "uploading_documents"
          ? t("requests.user.paymentResume.uploadingTitle", "Subiendo tus documentos")
          : paymentResumeProgress?.phase === "finalizing"
            ? t("requests.user.paymentResume.finalizingTitle", "Casi listo")
            : t("requests.user.paymentResume.confirmingTitle", "Confirmando tu pago");
    const resumeDescription =
      paymentResumeProgress?.phase === "uploading_documents"
        ? t("requests.user.paymentResume.uploadingDescription", "Documento {{current}} de {{total}} — no cierres esta pestaña.", {
            current: paymentResumeProgress.current,
            total: paymentResumeProgress.total,
          })
        : paymentResumeProgress?.phase === "creating_request"
          ? profileIncomplete
            ? t(
                "requests.user.paymentResume.savingDescription",
                "Estamos guardando tu solicitud. Ya casi — falta un paso rápido para enviarla.",
              )
            : t(
                "requests.user.paymentResume.creatingDescription",
                "Estamos registrando tu solicitud y vinculándola al pago.",
              )
          : paymentResumeProgress?.phase === "finalizing"
            ? profileIncomplete
              ? t(
                  "requests.user.paymentResume.finalizingProfileDescription",
                  "Un paso más para enviarla a un profesional.",
                )
              : t(
                  "requests.user.paymentResume.finalizingDescription",
                  "Confirmando todo con el profesional.",
                )
            : t(
                "requests.user.paymentResume.confirmingDescription",
                "Verificamos el pago con el procesador. Esto puede tomar unos segundos.",
              );
    return <LoadingPage title={resumeTitle} description={resumeDescription} />;
  }
  if (isLinkingChild) {
    return (
      <LoadingPage
        title={t(
          "requests.user.linkingChild.title",
          "Asociando tu solicitud a la gestión",
        )}
        description={t(
          "requests.user.linkingChild.description",
          "Un momento mientras vinculamos los datos.",
        )}
      />
    );
  }

  return (
    /**
     * The intake chat is the one step that takes the whole viewport.
     *
     * Everywhere else the wizard is a scrolling document under a fixed footer,
     * so the root reserves `pb-36` for that footer. The chat has no footer —
     * it is hidden below, because its own send button is the primary action
     * and a second louder button underneath only competed. Reserving space for
     * an absent bar left the composer floating in the middle of the page with
     * two thirds of it empty.
     *
     * `h-full` works because both layouts give `main` a definite height: an
     * `h-[100dvh]` flex column with `min-h-0 flex-1 overflow-y-auto` on the
     * main element (AppLayout and PublicWizardLayout alike). `min-h-0` has to
     * be repeated down the chain or the flex children refuse to shrink and the
     * composer is pushed off the bottom instead of the transcript scrolling.
     */
    <div
      ref={wizardScrollAnchorRef}
      className={cn(
        "flex min-h-0 flex-col",
        isQuoteIntakeChat ? "h-full" : "pb-36",
      )}
    >
      <div
        className={cn(
          "mx-auto w-full px-4 py-8 sm:px-6 lg:px-8",
          maxW,
          isQuoteIntakeChat && "flex min-h-0 flex-1 flex-col pb-6",
        )}
      >
        <WizardAnimatedPanel
          key={wizardPanelTransitionKey}
          className={cn(
            "space-y-8",
            isQuoteIntakeChat && "flex min-h-0 flex-1 flex-col",
          )}
        >
          <div className={cn("space-y-4", isQuoteIntakeChat && "shrink-0")}>
            {/*
              THE BACK CONTROL AND THE WAY OUT OF THE CHAT, on one row.

              "Prefiero completar un formulario" used to sit under the
              composer, where it was the last thing on the page and competed
              with the one action the screen exists for. It is an ESCAPE, not a
              step — the same weight as Volver — so it belongs up here beside
              it, leaving the bottom of the screen to the composer.

              `justify-between` with an empty span as the left child: the back
              button is absent for an anonymous visitor on the first step, and
              without a placeholder the link would slide left into the space
              where Volver normally sits.
            */}
            <div className="flex items-center justify-between gap-3">
              {showHeaderBack ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleBack}
                  disabled={isWizardSubmitting}
                  className="w-fit transition-colors duration-150"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t("pages.common.back")}
                </Button>
              ) : isAnonymous ? (
                <span />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/requests")}
                  className="w-fit transition-colors duration-150"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t("requests.user.wizard.backToList")}
                </Button>
              )}

              {/* Only while the chat can still take a turn — an exhausted or
                  finished conversation has nothing left to opt out of. */}
              {isQuoteIntakeChat && quoteIntakeTurnsRemaining > 0 ? (
                <button
                  type="button"
                  /* Wrapped, NOT passed by reference: this takes an optional
                     seed, and handing it an onClick would pass the MouseEvent
                     as the seed. */
                  onClick={() => fallbackToQuoteRequestForm()}
                  className="text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
                >
                  {t("requests.user.quoteIntakeUseForm")}
                </button>
              ) : null}
            </div>

            {isChooseIntent ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.intentLead")}
                rest={t("requests.user.wizard.intentRest")}
              />
            ) : null}
            {isChooseType ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.chooseTypeLead")}
                rest={t("requests.user.wizard.chooseTypeRest")}
              />
            ) : null}
            {isQuoteRequest ? (
              <WizardStepHeading
                lead={t(quoteRequestHeading.lead)}
                rest={t(quoteRequestHeading.rest)}
              />
            ) : null}
            {/*
              THE AI CAVEAT LIVES HERE NOW, and the chat's copy of it is gated
              to that screen's empty state because of this line. This is where
              the consumer ACTS on what the assistant produced — ChatGPT keeps
              its caveat under the composer because there every turn is the
              deliverable; here the deliverable is this draft.

              Only on a draft that CAME FROM the conversation, which
              `isQuoteDraftReviewStep` already encodes: `isQuoteDraftReview` IS
              `quoteIntakeDraftConversationId != null`. Someone who took the
              form route wrote every word themselves, and telling them an
              assistant might have erred would describe a step they skipped.
            */}
            {isQuoteDraftReviewStep ? (
              <p className="-mt-2 text-sm text-muted-foreground">
                {t("requests.user.quoteIntakeReviewCaveat")}
              </p>
            ) : null}
            {isChooseEnterpriseProduct ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.chooseEnterpriseProductLead")}
                rest={t("requests.user.wizard.chooseEnterpriseProductRest")}
              />
            ) : null}
            {isPreconditionCheck && selectedEnterpriseProduct ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.preconditionCheckTitle")}
                rest=""
              />
            ) : null}
            {isFillEnterpriseProduct && selectedEnterpriseProduct ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.fillEnterpriseProductLead")}
                rest={t("requests.user.wizard.fillEnterpriseProductRest")}
              />
            ) : null}
            {isLegalEntityFlow ? (
              <WizardStepHeading
                lead={
                  legalEntityPhase === "summary"
                    ? t("requests.user.wizard.legalEntitySummaryLead")
                    : t("requests.user.wizard.legalEntityLead")
                }
                rest={
                  legalEntityPhase === "summary"
                    ? t("requests.user.wizard.legalEntitySummaryRest")
                    : t("requests.user.wizard.legalEntityRest")
                }
              />
            ) : null}
            {isIncomeCertificateFlow && incomeReviewPhase === "summary" ? (
              <WizardStepHeading
                lead={t("requests.user.wizard.incomeSummaryLead")}
                rest={t("requests.user.wizard.incomeSummaryRest")}
              />
            ) : null}
          </div>

          <div
            className={cn(
              "space-y-10",
              isQuoteIntakeChat && "flex min-h-0 flex-1 flex-col",
              (isChooseIntent ||
                isChooseType ||
                isChooseEnterpriseProduct ||
                isPreconditionCheck ||
                isFillEnterpriseProduct ||
                isWorkflowSteps ||
                isLegalEntityFlow ||
                isIncomeCertificateFlow) &&
                "wizard-step-shell",
            )}
          >
            {isIncomeCertificateFlow ? (
              <>
                <div className={cn(incomeReviewPhase === "summary" && "hidden")}>
                  <IncomeCertificateWizard
                    ref={incomeWizardRef}
                    allowedFulfillmentModes={incomeFulfillmentModes}
                    form={incomeForm}
                    innerStep={incomeInnerStep}
                    onInnerStepChange={setIncomeInnerStep}
                    onPrimaryEnabledChange={setIncomePrimaryEnabled}
                    onSuccess={(id) => {
                      navigate(`/requests/${id}?source=accountant`);
                    }}
                    createWithUpload={(p) => submitIncomeCertificate(p)}
                    onStep3ContinueToReview={handleIncomeStep3ContinueToReview}
                  />
                </div>
                {incomeReviewPhase === "summary" && incomeReviewPayload != null ? (
                  <IncomeCertificateRequestSummary
                    payload={incomeReviewPayload}
                    totalChargeLabel={incomeChargeTotalLabel}
                    catalogItem={incomeCertificateCatalogItem}
                    invoiceIncludeRut={effectiveIncomeInvoiceIncludeRut}
                    onInvoiceIncludeRutChange={setIncomeInvoiceIncludeRut}
                    invoiceRutDigits={effectiveIncomeInvoiceRutDigits}
                    onInvoiceRutDigitsChange={setIncomeInvoiceRutDigits}
                    invoiceRutLockedFromRegistration={invoiceRutLockedToRegistration}
                  />
                ) : null}
                {incomeReviewPhase === "summary" && incomeReviewPayload != null ? (
                  <PaymentMethodSelector
                    value={paymentMethod}
                    onChange={setPaymentMethod}
                    disabled={isCertificatePaymentBusy}
                  />
                ) : null}
              </>
            ) : null}

            {isChooseIntent ? (
              <NewRequestIntentSelection
                intents={visibleIntents}
                isLoading={certificateRequestIntentsQuery.isPending}
                value={selectedIntentSlug}
                onValueChange={handleIntentChange}
              />
            ) : null}

            {isChooseType && allowedTypes.length > 0 ? (
              <NewRequestTypeSelection
                allowedTypes={allowedTypes}
                value={requestType}
                onValueChange={handleCertificateTypeChange}
                catalogItems={catalogQuery.data}
                showPricing={false}
              />
            ) : null}

            {isChooseEnterpriseProduct ? (
              enterpriseCatalogQuery.isPending ? (
                <div className="flex min-h-[20vh] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : enterpriseFormSubmitting ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Creando solicitud...</p>
                </div>
              ) : enterpriseCatalogQuery.data && enterpriseCatalogQuery.data.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {enterpriseCatalogQuery.data.map((entry) => (
                    <EnterpriseProductCard
                      key={entry.slug}
                      entry={entry}
                      onClick={() => handleSelectEnterpriseProduct(entry)}
                    />
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-muted-foreground">
                  {t("requests.user.wizard.noEnterpriseProducts")}
                </p>
              )
            ) : null}

            {isWorkflowSteps && selectedEnterpriseProduct?.graph ? (
              enterpriseFormSubmitting || (workflowRunnerState.currentNodeId &&
              getNodeById(selectedEnterpriseProduct.graph as ProductGraph, workflowRunnerState.currentNodeId)?.type === 'createRequest') ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                  <div className="text-center space-y-1">
                    <p className="text-base font-medium text-foreground">
                      {t("requests.user.wizard.enterpriseCreatingTitle")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t("requests.user.wizard.enterpriseCreatingHint")}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {workflowStepHeading && (
                    <WizardStepHeading lead={workflowStepHeading.lead} rest={workflowStepHeading.rest} />
                  )}
                  <GraphWorkflowRunner
                    graph={selectedEnterpriseProduct.graph}
                    state={workflowRunnerState}
                    enterpriseRequestId={activeEnterpriseRequest?.id ?? ""}
                    allPayloadValues={activeEnterpriseRequest?.payload ?? pendingStepData}
                    onCompleteNode={handleCompleteWorkflowStep}
                    onBack={() => setShowCancelDialog(true)}
                    hideStepper
                    hideHeading
                  />
                </>
              )
            ) : null}

            {isLegalEntityFlow && legalEntityPhase === "form" ? (
              <div className="space-y-3">
                <Label className="text-foreground">{t("requests.user.fulfillmentMode.sectionTitle")}</Label>
                <p className="text-sm text-muted-foreground">{t("requests.user.fulfillmentMode.sectionHint")}</p>
                <RadioGroup
                  value={legalEntityFulfillmentMode}
                  onValueChange={setLegalEntityFulfillmentMode}
                  className="flex flex-col gap-3"
                >
                  {legalEntityFulfillmentOptions.map((slug) => (
                    <label
                      key={slug}
                      htmlFor={`le-fulfillment-${slug}`}
                      className="wizard-option-row"
                    >
                      <RadioGroupItem value={slug} id={`le-fulfillment-${slug}`} className="mt-0.5" />
                      <span className="text-sm leading-snug text-foreground">
                        {t(`requests.user.fulfillmentMode.option.${slug}`)}
                      </span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            ) : null}
            {isLegalEntityFlow && legalEntityPhase === "form" ? (
              <LegalEntityRequestFields
                legalEntityCompanyRutMode={legalEntityCompanyRutMode}
                registeredCompanySnapshot={registeredCompanySnapshot}
                onSelectSessionCompany={selectLegalEntitySessionCompany}
                onSelectOtherCompany={selectLegalEntityOtherCompany}
                onReturnToCompanyChoice={returnToLegalEntityCompanyChoice}
                rut={rut}
                onRutChange={handleRutInputChange}
                onValidateRut={() => void handleValidateRut()}
                isValidatingRut={isValidatingRut}
                validatedCompany={validatedCompany}
                manualMode={legalEntityManualMode}
                manualFields={legalEntityManualFields}
                onManualFieldChange={updateLegalEntityManualField}
                companyPreview={legalEntityEffectiveCompany}
                estatutoFile={estatutoFile}
                onEstatutoChange={setEstatutoFile}
                comunicacionBcuFile={comunicacionBcuFile}
                onComunicacionBcuChange={setComunicacionBcuFile}
                legalDocumentType={legalDocumentType}
                onLegalDocumentTypeChange={setLegalDocumentType}
                legalDocumentFrontFile={legalDocumentFrontFile}
                onLegalDocumentFrontChange={setLegalDocumentFrontFile}
                legalDocumentBackFile={legalDocumentBackFile}
                onLegalDocumentBackChange={setLegalDocumentBackFile}
                passportDocumentFile={passportDocumentFile}
                onPassportDocumentChange={setPassportDocumentFile}
              />
            ) : null}
            {isLegalEntityFlow &&
            legalEntityPhase === "summary" &&
            legalEntityEffectiveCompany != null &&
            estatutoFile != null &&
            comunicacionBcuFile != null ? (
              <LegalEntityRequestSummary
                rutDigits={rut}
                validatedCompany={legalEntityEffectiveCompany}
                companyRutUnverified={legalEntityManualMode}
                legalDocumentType={legalDocumentType}
                estatutoFile={estatutoFile}
                comunicacionBcuFile={comunicacionBcuFile}
                legalDocumentFrontFile={legalDocumentFrontFile}
                legalDocumentBackFile={legalDocumentBackFile}
                passportDocumentFile={passportDocumentFile}
                totalChargeLabel={legalEntityChargeTotalLabel}
                catalogItem={legalEntityCatalogItem}
                fulfillmentMode={legalEntityFulfillmentMode}
                invoiceIncludeRut={effectiveLegalEntityInvoiceIncludeRut}
                onInvoiceIncludeRutChange={setLegalEntityInvoiceIncludeRut}
                invoiceRutDigits={effectiveLegalEntityInvoiceRutDigits}
                onInvoiceRutDigitsChange={setLegalEntityInvoiceRutDigits}
                invoiceRutLockedFromRegistration={invoiceRutLockedToRegistration}
              />
            ) : null}

            {isLegalEntityFlow && legalEntityPhase === "summary" ? (
              <PaymentMethodSelector
                value={paymentMethod}
                onChange={setPaymentMethod}
                disabled={isCertificatePaymentBusy}
              />
            ) : null}

            {isQuoteRequest ? (
              quoteIntakeOutOfScopeMessage ? (
                <QuoteIntakeOutOfScope
                  message={quoteIntakeOutOfScopeMessage}
                  onBack={() => navigate("/requests")}
                  // The primary way out is the FORM, not the door: a wrong
                  // verdict must cost a detour, never the job.
                  onSendAnyway={sendOutOfScopeAnyway}
                  // Reuses the LiveChat widget the app already ships rather
                  // than inventing a support URL. Guarded because the widget
                  // is license-gated and simply absent in some environments —
                  // which is precisely why it cannot be the only way out.
                  onContact={() => window.LiveChatWidget?.call("maximize")}
                />
              ) : quoteRequestPhase === "chat" ? (
                <QuoteIntakeChat
                  messages={quoteIntakeMessages}
                  isThinking={quoteIntakeThinking}
                  canSend={quoteIntakeTurnsRemaining > 0}
                  turnsRemaining={quoteIntakeTurnsRemaining}
                  /* NO LONGER GATED (spec b5b59843). The account is asked for
                     at the draft-review step, once the assistant has worked
                     out what the visitor needs and there is something to lose
                     by walking away. An anonymous send mints a guest identity
                     server-side; see startQuoteIntake. */
                  onSend={(content, captchaToken) =>
                    void handleQuoteIntakeSend(content, captchaToken)
                  }
                  onUseForm={fallbackToQuoteRequestForm}
                  initialDraft={heroAskDraft}
                  /* The next message will MINT a guest identity, so it is the
                     one that owes a captcha (control C-6). Both halves matter:
                     a signed-in visitor never mints one, and once the
                     conversation exists the guest token is the credential. */
                  requireCaptcha={user === null && quoteIntakeConversationId === null}
                />
              ) : quoteRequestPhase === "form" ? (
                // Two genuinely different screens behind one phase. The draft
                // path is a SUMMARY to check — nothing on it was typed by the
                // consumer. The fallback path really is a form, because the
                // model was unavailable and they have to write it themselves.
                isQuoteDraftReview ? (
                  <QuoteRequestReview
                    title={quoteRequestTitle}
                    onTitleChange={setQuoteRequestTitle}
                    description={quoteRequestDescription}
                    onDescriptionChange={setQuoteRequestDescription}
                    attachments={quoteRequestAttachments}
                    onAttachmentsChange={setQuoteRequestAttachments}
                    anticipatedDocuments={quoteAnticipatedDocuments}
                    intakeAnswers={quoteIntakeAnswers}
                    onIntakeAnswersChange={setQuoteIntakeAnswers}
                    disciplines={quoteDraftDisciplines}
                  />
                ) : (
                  <QuoteRequestFields
                    title={quoteRequestTitle}
                    onTitleChange={setQuoteRequestTitle}
                    description={quoteRequestDescription}
                    onDescriptionChange={setQuoteRequestDescription}
                    attachments={quoteRequestAttachments}
                    onAttachmentsChange={setQuoteRequestAttachments}
                    anticipatedDocuments={quoteAnticipatedDocuments}
                    intakeAnswers={quoteIntakeAnswers}
                    onIntakeAnswersChange={setQuoteIntakeAnswers}
                  />
                )
              ) : quoteRequestPhase === "delivery" ? (
                <QuoteDeliveryAddressStep
                  selection={quoteDeliveryAddressSelection}
                  onSelectionChange={setQuoteDeliveryAddressSelection}
                  optedOut={quoteDeliveryOptedOut}
                  onOptedOutChange={setQuoteDeliveryOptedOut}
                  disabled={isWizardSubmitting || isSavingDeliveryAddress}
                  onPriceabilityChange={setIsDeliveryUnpriceable}
                />
              ) : (
                <div className="space-y-4">
                  <QuoteRequestSummaryStep
                    title={quoteRequestTitle}
                    description={quoteRequestDescription}
                    attachments={quoteRequestAttachments}
                    intakeDetails={quoteIntakeDetails}
                    disciplines={quoteDraftDisciplines}
                  />
                  {/* Submission happens from this step, so the refusal belongs
                      here — next to the text it is about, and still on screen
                      while the consumer goes back to edit it. */}
                  {quoteRequestBlockedKinds ? (
                    <ContactBlockedNotice
                      kinds={quoteRequestBlockedKinds}
                      titleKey="requests.user.quoteRequestBlockedTitle"
                    />
                  ) : null}
                </div>
              )
            ) : null}
          </div>
        </WizardAnimatedPanel>
      </div>

      <div
        className={cn(
          "fixed bottom-0 right-0 z-10 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80",
          "left-0",
          /*
            Only offset for a sidebar that is actually drawn, and ask the SHELL
            rather than inferring it.

            This read `!isAnonymous` until lazy registration made that proxy
            false: RequestsNewWizardRoute locks the shell at first render — on
            purpose, so signing up mid-wizard does not unmount the tree — so a
            visitor who registers at the draft-review gate stays in
            PublicWizardLayout while `user` becomes non-null. The footer then
            reserved 16rem for a sidebar that was never rendered, and the
            footer and the content column visibly fell out of alignment.
          */
          hasSidebar && (isSidebarOpen ? "lg:left-64" : "lg:left-16"),
          // The chat owns the full viewport and its own controls: the send
          // button is the primary action and the form opt-out sits under the
          // composer, so the bar below would be an empty strip with a Cancel
          // in it, eating the height the conversation wants.
          (isFillEnterpriseProduct ||
            isPreconditionCheck ||
            isWorkflowSteps ||
            isQuoteIntakeChat) &&
            "hidden",
        )}
      >
        {/*
          Match sidebar user strip: border-t + p-3, default buttons (h-10) like Header/Sidebar account trigger.
        */}
        <div className={cn("mx-auto w-full px-3 py-3 sm:px-6 lg:px-8", maxW)}>
          <div className="flex min-h-12 flex-row items-center justify-between gap-3">
              {!isChooseIntent ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={isWizardSubmitting}
                  className="transition-colors duration-150"
                >
                  {t("pages.common.cancel")}
                </Button>
              ) : <div />}
              {isIncomeCertificateFlow ? (
                <Button
                  type="button"
                  onClick={() => {
                    if (incomeReviewPhase === "summary") {
                      gateOnAuth(() => completeIncomeCertificateFromSummary());
                    } else {
                      void runIncomePrimaryAction();
                    }
                  }}
                  disabled={footerPrimaryDisabled}
                  className="min-w-[9rem] transition-colors duration-150"
                >
                  {isWizardSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                      {footerPrimaryLabel}
                    </>
                  ) : (
                    footerPrimaryLabel
                  )}
                </Button>
              ) : isQuoteIntakeChat ? null : (
                <Button
                  type="button"
                  onClick={handleFooterPrimary}
                  disabled={footerPrimaryDisabled}
                  className="min-w-[9rem] transition-colors duration-150"
                >
                  {nonIncomeSubmittingSpinner ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                      {footerPrimaryLabel}
                    </>
                  ) : (
                    footerPrimaryLabel
                  )}
                </Button>
              )}
          </div>
        </div>
      </div>
      <AuthModal
        open={authModalOpen}
        surface="wizard_submit"
        forceProcessing={isPostAuthSubmitting}
        onOpenChange={(open) => {
          // Block close attempts while the post-auth submit is in flight — the
          // forceProcessing branch already ignores Escape / outside-click, but this
          // is a belt-and-braces against any other close path.
          if (isPostAuthSubmitting && !open) return;
          /*
            THE BOUNCE (spec rfq-auth-gate-instrumentation).

            Sound here precisely because `onSuccess` below closes the modal by
            calling `setAuthModalOpen(false)` ITSELF and never routes through
            this handler — verified, theory 89573ac. If a later edit moves that
            close here, every conversion starts reporting a dismissal too and
            the bounce rate pins near 100% while still looking plausible; the
            spec beside this file fails on exactly that change.

            After the early return above, `!open` can only be a visitor closing
            the wall: Escape, outside click, or the close button.
          */
          if (!open) {
            trackRfqAuthGateDismissed({ fromIntake: authGateFromIntake.current });
          }
          setAuthModalOpen(open);
          if (!open) pendingPostAuthAction.current = null;
        }}
        onSuccess={(authPath) => {
          if (authPath === "register") {
            fireRegistrationConversion();
          }
          /*
            Got past the wall. Paired with the dismissal above, this is what
            turns the bounce into a RATE rather than a count — without it a
            quiet week and a week where everyone converted look the same.
          */
          trackRfqAuthGateCompleted({
            fromIntake: authGateFromIntake.current,
            authPath,
          });
          /*
            CARRY THE CONVERSATION ACROSS (spec b5b59843, controls C-4/C-5).

            Two paths, because they are genuinely different. REGISTER promoted
            the guest row in place server-side — same user id — so the
            conversation is already theirs and there is nothing to move; the
            token is simply spent. SIGN-IN could not: that visitor already had
            an account, so the work has to be claimed onto it.

            Fire-and-forget on purpose. The visitor is mid-flow and the next
            thing that happens is their request being prepared; a failed claim
            costs them the classifier re-running on text they already wrote,
            which is a worse quote, not a broken one. Blocking the wizard on it
            — or surfacing an error — would trade a small loss for a visible
            failure at the exact moment they committed.
          */
          void settleGuestIntake(authPath);
          const action = pendingPostAuthAction.current;
          pendingPostAuthAction.current = null;
          if (!action) {
            setAuthModalOpen(false);
            return;
          }
          // Flip the modal into the "Preparando tu solicitud" loader so the visitor
          // sees continuous progress through register → submit → dLocal redirect.
          // The modal stays mounted until either the dLocal redirect navigates the
          // browser away or the action resolves cleanly (in-app navigation path) /
          // throws silently (failure path, handled by toast inside the hook).
          setIsPostAuthSubmitting(true);
          // Defer to the next microtask so React has flushed the auth context update
          // before the captured action re-reads useAuth / isAuthenticated.
          setTimeout(() => {
            // Not an async callback: setTimeout discards the promise, so a
            // rejection from `action()` escaped as an unhandled rejection.
            // The hook already reports failures by toast, so catching here
            // changes nothing a user sees — it just stops the rejection
            // leaking out of the timer. `finally` still runs either way.
            // Promise.resolve because `action` is typed void | Promise<void>
            // — it may be synchronous.
            void Promise.resolve(action())
              .catch(() => undefined)
              .finally(() => {
                // If we're still mounted (no navigation, no redirect — i.e. the
                // action failed and showed a toast), reset so the visitor can
                // retry from the wizard. The browser-leave / in-app navigate
                // paths unmount us before this fires.
                setIsPostAuthSubmitting(false);
                setAuthModalOpen(false);
              });
          }, 0);
        }}
        onBeforeSsoRedirect={() => {
          snapshotWizardForSsoRedirect();
        }}
        ssoReturnTo="/requests/new"
      />
      <ConfirmationDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title={t("requests.user.wizard.cancelDialog.title")}
        description={t("requests.user.wizard.cancelDialog.description")}
        confirmText={t("requests.user.wizard.cancelDialog.confirm")}
        cancelText={t("requests.user.wizard.cancelDialog.dismiss")}
        variant="destructive"
        onConfirm={() => {
          setShowCancelDialog(false);
          // Wipe any IndexedDB snapshot so the next anonymous visit starts clean.
          clearPersistedWizardSnapshot();
          // Enterprise workflows land back on the enterprise tab. Authenticated
          // consumers land on their requests list. Anonymous guest-checkout
          // visitors restart the wizard at /requests/new — the portal SPA root
          // is auth-gated and would bounce them to /login otherwise.
          if (returnEnterpriseRequestId) {
            navigate("/requests?tab=enterprise");
          } else if (isAnonymous) {
            /*
              A DOCUMENT LOAD, NOT A ROUTER NAVIGATION, and the difference is
              the whole bug: an anonymous visitor is ALREADY on /requests/new,
              so `navigate` there kept this component mounted. Every piece of
              wizard state — the transcript, the step, the draft, the
              attachments — lives in useState inside it, so Descartar cleared
              the IndexedDB snapshot, closed the dialog, and left the screen
              exactly as it was. Reported as "cancel then discard does
              nothing".

              The other two branches are unaffected because they leave the
              route, which unmounts the wizard and takes its state with it.

              A reload is the honest implementation of "discard everything":
              it cannot miss a state atom the way a hand-written reset through
              a hook this size would, and this is the one action where paying
              a page load is exactly what the user asked for.
            */
            window.location.assign("/requests/new");
          } else {
            navigate("/requests");
          }
        }}
      />
    </div>
  );
}
